/**
 * ページ上の Algolia 駆動セクションを multipleQueries で 1 リクエストに集約する共有 orchestrator。
 * TOP / PDP / 固定ページ（pages）で共通利用する（実体は 1 コンポーネント）。
 *
 * 課題: 1 ページに複数の Algolia セクション（商品カルーセル×N / カテゴリ別ランキングタブ /
 * 最近チェック）が並列配置され、各々が自前で search() を発火するとセクション数ぶん
 * 並列リクエストになり、課金とキャッシュ効率の両方で不利（実測 2〜3 リクエスト/PV）。
 *
 * 解決: orchestrator が body 末尾に置かれ、HTML パース順で先にロードされた
 * deferred モードの各セクションを document scope で集約し、全クエリを 1 つの
 * multipleQueries 配列に連結して 1 リクエストに束ねる。results は順序 1:1 対応なので、
 * carousel には applyExternalHits()、ranking-tabs / recently-viewed には
 * applyDeferredResults() でスライスを返し、Liquid テンプレートに描画させる。
 *
 * 配置場所（いずれもセクションが DOM に揃った後の </main> / body 末尾）:
 *   - TOP: layout/v2-theme.liquid の template == 'index' 分岐（静的直書き）
 *   - PDP: layout/v2-theme.liquid の template == 'product' 分岐（静的直書き）
 *   - 固定ページ: snippets/v2-page-carousel-runtime.liquid が carousel 検出時に動的注入
 * HTML パース順序で「全セクションが DOM に揃った後」に connectedCallback が
 * 走ることを利用し、microtask 待ちや MutationObserver を持ち込まない設計
 * （配置で解決できる問題は配置で解決する原則）。
 * ⚠️ この順序保証のため chunks/algolia.ts では orchestrator を最後に export し、
 *    全 participant の customElements.define() より後に define されるようにすること。
 *
 * Lit は振る舞いレイヤーに徹し HTML は持たない（render() = nothing、Light DOM）。
 */
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import type { AlgoliaHit } from './algolia-hit-template';
import {
  getAlgoliaConfig,
  createSearchClient,
  waitForAlgoliaLibs,
} from './utils/algolia-client';
import { buildBatchQueriesForCarousels } from './orchestrator.logic';
import type { KdAlgoliaCarousel } from './kd-algolia-carousel';

interface SearchResult {
  hits: AlgoliaHit[];
  nbHits: number;
}

/**
 * orchestrator が 1 リクエストに同梱する deferred コンポーネントの契約。
 * kd-algolia-ranking-tabs が data-deferred 時に実装する。
 * - getDeferredQueries: 自身の multipleQueries 配列を返す（無効時は [] を返し自身で section 非表示）。
 * - applyDeferredResults: 自身分の results スライス（順序対応）を受け取り描画する。
 * - handleDeferredFailure: バッチ失敗時に section を畳む。
 *
 * クエリ形式は異種（carousel=フィルタ検索 / ranking=タブ別 / recently-viewed=handle 全文）だが、
 * いずれも `{ indexName, params }` 形なので 1 つの multipleQueries 配列に連結でき、
 * results は順序 1:1 対応なのでスライスで各コンポーネントへ返せる。
 */
interface DeferredParticipant {
  getDeferredQueries(): Array<{ indexName: string; params: unknown }>;
  applyDeferredResults(results: Array<SearchResult | undefined>): void;
  handleDeferredFailure(): void;
}

@customElement('kd-algolia-orchestrator')
export class KdAlgoliaOrchestrator extends LitElement {
  createRenderRoot() {
    return this;
  }

  private _loaded = false;

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    // Shopify テーマエディタで section をドラッグ移動すると connectedCallback が
    // 再発火するため二重起動を防ぐ
    if (this._loaded) return;
    this._loaded = true;
    await this._loadAll();
  }

  private async _loadAll(): Promise<void> {
    const carousels = this._collectCarousels();
    const participants = this._collectParticipants();
    if (carousels.length === 0 && participants.length === 0) return;

    const config = getAlgoliaConfig();
    if (!config?.connected) {
      this._failAll(carousels, participants);
      return;
    }

    try {
      await waitForAlgoliaLibs();
      const searchClient = createSearchClient({
        // コンポーネント単位のキー命名に揃える（他: -carousel / -ranking-tabs 等）。
        // top固有名→汎用名リネームの残りで、旧 'algolia-responses-top' は
        // kd-algolia-top-rankings と衝突していたため専用プールへ分離する。
        cacheKey: 'algolia-responses-orchestrator',
        maxEntries: 50,
      });
      if (!searchClient) {
        this._failAll(carousels, participants);
        return;
      }

      const carouselQueries = buildBatchQueriesForCarousels(
        carousels.map((c) => c.getQueryConfig()),
        config.indexName,
        config.customer,
      );

      // carousel クエリ + 各 participant のクエリを 1 配列に連結する。
      // 先頭 carouselQueries.length 個が carousels[i] に 1:1 対応し、
      // 続く各 participant 分は控えた [start, start+count) のスライスで返す。
      const queries: Array<{ indexName: string; params: unknown }> = [...carouselQueries];
      const segments: Array<{
        participant: DeferredParticipant;
        start: number;
        count: number;
      }> = [];
      for (const participant of participants) {
        // 無効な participant（履歴なし等）は [] を返し自身で section を非表示にしている。
        const participantQueries = participant.getDeferredQueries();
        if (participantQueries.length === 0) continue;
        segments.push({
          participant,
          start: queries.length,
          count: participantQueries.length,
        });
        queries.push(...participantQueries);
      }

      // carousels 0 件かつ有効 participant 0 件なら何もしない。
      if (queries.length === 0) return;

      const response = (await searchClient.search(queries)) as {
        results: SearchResult[];
      };

      // results 配列長と queries 配列長が一致しなければ defensive に失敗扱い。
      // Algolia 側の異常レスポンスで一部の hits だけ欠ける状況を想定し、
      // 中途半端な描画より「全部 fail で section を畳む」方が運用上扱いやすい。
      if (response.results.length !== queries.length) {
        this._failAll(carousels, participants);
        return;
      }

      // carousels: 先頭 carouselQueries.length 個が 1:1 対応。
      carousels.forEach((carousel, i) => {
        carousel.applyExternalHits(response.results[i]?.hits ?? [], response.results[i]?.nbHits);
      });
      // participants: 控えたスライス範囲を渡す。
      for (const seg of segments) {
        seg.participant.applyDeferredResults(
          response.results.slice(seg.start, seg.start + seg.count),
        );
      }
    } catch {
      this._failAll(carousels, participants);
    }
  }

  /**
   * 自前 search に fallback すると各セクションが並列リクエストを発火して
   * Algolia 課金倍増の元凶が再発するため、batch 失敗時は全 carousel /
   * participant の失敗ハンドラを呼んで section を畳む。
   */
  private _failAll(
    carousels: KdAlgoliaCarousel[],
    participants: DeferredParticipant[],
  ): void {
    carousels.forEach((c) => c.handleOrchestratorFailure());
    participants.forEach((p) => p.handleDeferredFailure());
  }

  /**
   * data-deferred な ranking-tabs を集約対象として収集する。
   * これらは connectedCallback で自前 search せず orchestrator の駆動を待つ。
   * （最近チェック kd-recently-viewed-carousel は Storefront 移設で orchestrator 集約から外れ、
   *   自前で Shopify から取得する。これによりバッチキーが安定しキャッシュが効く。）
   * orchestrator は chunks/algolia.ts で最後に定義され、document 末尾に配置されるため、
   * この時点で全 participant は既に upgrade + connect 済み。
   */
  private _collectParticipants(): DeferredParticipant[] {
    return Array.from(
      document.querySelectorAll('kd-algolia-ranking-tabs[data-deferred]'),
    ) as unknown as DeferredParticipant[];
  }

  /**
   * deferred モードの carousel のみを集約対象とする。
   * data-deferred 属性が無い carousel（他ページから流用された場合等）は
   * 自前 search モードで動き続けるため、ここでは触らない。
   *
   * セレクタは属性 presence (`[data-deferred]`) で照合する。
   * `kd-algolia-carousel.deferred` は `@property reflect: true` のため、
   * Lit が boolean true を反射すると attribute 値が `""` になり、
   * `[data-deferred="true"]` だと TS から `el.deferred = true` を書いた瞬間に
   * セレクタが外れる脆い契約になる。presence 判定なら Liquid 直書きでも
   * Lit reflect 後でも一貫してマッチする。
   */
  private _collectCarousels(): KdAlgoliaCarousel[] {
    return Array.from(
      document.querySelectorAll<KdAlgoliaCarousel>('kd-algolia-carousel[data-deferred]'),
    );
  }

  render() {
    return nothing;
  }
}
